import type { NonEmptyArray } from '@ardoq/common-helpers';
import type { SlideImage } from './slidePlan';

type Size = { w: number; h: number };

export type SliceRange = { top: number; height: number };

type RowScoreArgs = {
  data: Uint8ClampedArray;
  width: number;
  step: number;
};

type CutRowArgs = {
  context: CanvasRenderingContext2D;
  idealRow: number;
  radius: number;
};

export const MAX_TALLNESS = 2;
export const MAX_SLICES = 30;
const CUT_WINDOW_SHARE = 0.1;
const MAX_CUT_RADIUS_PX = 64;
const SAMPLE_STEP_PX = 2;
const CHANNELS = 4;

export const toSliceCount = (image: Size, box: Size): number => {
  const tallness = (image.h / image.w) * (box.w / box.h);
  return Math.min(MAX_SLICES, Math.max(1, Math.ceil(tallness / MAX_TALLNESS)));
};

export const toIdealCuts = (image: Size, box: Size): number[] => {
  const count = toSliceCount(image, box);
  return Array.from({ length: count - 1 }, (_, index) =>
    Math.round(((index + 1) * image.h) / count)
  );
};

export const toCutRadius = (height: number, sliceCount: number): number =>
  Math.min(
    MAX_CUT_RADIUS_PX,
    Math.floor((height / sliceCount) * CUT_WINDOW_SHARE)
  );

const toRowScore = (
  { data, width, step }: RowScoreArgs,
  row: number
): number => {
  const rowData = data.subarray(
    row * width * CHANNELS,
    (row + 1) * width * CHANNELS
  );
  const offset = step * CHANNELS;
  return rowData.reduce((sum, value, index) => {
    const pixel = Math.floor(index / CHANNELS);
    return pixel % step === 0 && pixel + step < width
      ? sum + Math.abs(value - rowData[index + offset])
      : sum;
  }, 0);
};

export const toRowScores = (args: RowScoreArgs): number[] =>
  Array.from({ length: args.data.length / (args.width * CHANNELS) }, (_, row) =>
    toRowScore(args, row)
  );

export const toBestCutOffset = (scores: number[]): number => {
  const center = (scores.length - 1) / 2;
  return scores.reduce(
    (best, score, index) =>
      score < scores[best] ||
      (score === scores[best] &&
        Math.abs(index - center) < Math.abs(best - center))
        ? index
        : best,
    0
  );
};

export const toSliceRanges = (
  height: number,
  cuts: number[]
): NonEmptyArray<SliceRange> => [
  { top: 0, height: cuts[0] ?? height },
  ...cuts.map((top, index) => ({
    top,
    height: (cuts[index + 1] ?? height) - top,
  })),
];

export const toContext = (
  canvas: HTMLCanvasElement
): CanvasRenderingContext2D => {
  const context = canvas.getContext('2d');
  if (!context) {
    throw Error('cannot create a 2d context');
  }
  return context;
};

export const toCutRow = ({ context, idealRow, radius }: CutRowArgs): number => {
  const top = idealRow - radius;
  const { width } = context.canvas;
  const { data } = context.getImageData(0, top, width, radius * 2 + 1);
  return (
    top + toBestCutOffset(toRowScores({ data, width, step: SAMPLE_STEP_PX }))
  );
};

export const toStackedImage = (
  source: HTMLCanvasElement,
  bands: SliceRange[]
): SlideImage => {
  const canvas = document.createElement('canvas');
  canvas.width = source.width;
  canvas.height = bands.reduce((sum, { height }) => sum + height, 0);
  const context = toContext(canvas);
  const offsets = bands.map((_, index) =>
    bands.slice(0, index).reduce((sum, { height }) => sum + height, 0)
  );
  bands.forEach(({ top, height }, index) =>
    context.drawImage(
      source,
      0,
      top,
      source.width,
      height,
      0,
      offsets[index],
      source.width,
      height
    )
  );
  return {
    dataUrl: canvas.toDataURL('image/png'),
    w: canvas.width,
    h: canvas.height,
  };
};

export const toSliceImages = (
  context: CanvasRenderingContext2D,
  box: Size
): NonEmptyArray<SlideImage> => {
  const { canvas } = context;
  const { width: w, height: h } = canvas;
  const idealCuts = toIdealCuts({ w, h }, box);
  if (idealCuts.length === 0) {
    return [{ dataUrl: canvas.toDataURL('image/png'), w, h }];
  }
  const radius = toCutRadius(h, idealCuts.length + 1);
  const cuts = idealCuts.map(idealRow =>
    toCutRow({ context, idealRow, radius })
  );
  const toSliceImage = (range: SliceRange): SlideImage =>
    toStackedImage(canvas, [range]);
  const [first, ...rest] = toSliceRanges(h, cuts);
  return [toSliceImage(first), ...rest.map(toSliceImage)];
};
